import React, { memo } from "react";
import { HiMinus, HiPlus } from "react-icons/hi";
import { TicketTypeWithId } from "../../../hooks/useEventCardState";
import { CustomButton } from "../CustomButton";

interface QuantitySelectorProps {
  ticket: TicketTypeWithId;
  quantity: number;
  remaining: number | null;
  onQuantityChange: (value: number) => void;
  onProceed: () => void;
  onBack: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

const QuantitySelector: React.FC<QuantitySelectorProps> = memo(
  ({ ticket, quantity, remaining, onQuantityChange, onProceed, onBack, t }) => {
    const min = ticket.settings.min_per_order || 1;
    const max =
      ticket.settings.max_per_order || (remaining !== null ? remaining : 2);

    const canDecrease = quantity > min;
    const canIncrease = quantity < max;

    const handleDecrease = () => {
      if (canDecrease) {
        onQuantityChange(quantity - 1);
      }
    };

    const handleIncrease = () => {
      if (canIncrease) {
        onQuantityChange(quantity + 1);
      }
    };

    return (
      <div className="flex flex-col gap-4 p-4">
        {/* Header */}
        <div className="text-center">
          <p className="text-sm text-white/70 mt-1">
            {t("event.tickets.selectQuantity")}
          </p>
        </div>

        {/* Quantity Stepper */}
        <div className="flex items-center justify-center gap-6 py-2">
          <button
            onClick={handleDecrease}
            disabled={!canDecrease}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
              canDecrease
                ? "bg-white/20 hover:bg-white/30 active:scale-95"
                : "bg-white/5 cursor-not-allowed opacity-40"
            }`}
            aria-label={t("event.tickets.decreaseQuantity")}
          >
            <HiMinus className="text-white" size={20} />
          </button>

          <div className="flex flex-col items-center min-w-[80px]">
            <span className="text-2xl font-bold text-white">{quantity}</span>
          </div>

          <button
            onClick={handleIncrease}
            disabled={!canIncrease}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
              canIncrease
                ? "bg-white/20 hover:bg-white/30 active:scale-95"
                : "bg-white/5 cursor-not-allowed opacity-40"
            }`}
            aria-label={t("event.tickets.increaseQuantity")}
          >
            <HiPlus className="text-white" size={20} />
          </button>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 mt-2">
          <CustomButton
            title={t("event.tickets.back")}
            onClick={onBack}
            variant="secondary"
            className="flex-1 bg-white/10 border-white/20 text-white enabled:hover:bg-white/20"
          />
          <CustomButton
            title={t("event.tickets.next")}
            onClick={onProceed}
            variant="primary"
            className="flex-1 bg-white text-gray-900 enabled:hover:bg-gray-100 font-semibold shadow-lg"
          />
        </div>
      </div>
    );
  }
);

QuantitySelector.displayName = "QuantitySelector";

export default QuantitySelector;
